import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initTutorialsAction } from '../../tutorialsReducer';
import { getTutorialsListSelector } from '../../tutorialsSelectors';
import { TTutorialType } from '../../tutorials.t';
import { TUTORIAL_TYPES } from '../../tutorialsConstants';
import getAcademyLessonVideoPreviewImgSrcByUrl from '../../../Academy/utils/getAcademyLessonVideoPreviewImgSrcByUrl';
import {
    TutorialListWidgetCardsScrollerStyled,
    TutorialListWidgetCardsStyled,
    TutorialListWidgetCardStyled,
    TutorialListWidgetHeaderStyled,
    TutorialListWidgetItemInfoStyled,
    TutorialListWidgetItemTitleStyled,
} from './styled/TutorialListWidgetCardsStyled';
import classNames from 'classnames';
import { useLangContext } from '../../../../providers/LangProvider/LangProviderContext';
import TutorialPlayIcon from '../TutorialPlayIcon/TutorialPlayIcon';
import SeeAllButton from '../../../../components/SeeAllButton/SeeAllButton';
import { locationSlice } from '../../../Location/locationReducer';
import { LOCATION_NAMES } from '../../../Location/locationConstants';
import useHorizontalScroll from '../../../../hooks/useHorizontalScroll';
import { getIsLocationPlatformMobileSelector } from '../../../Location/locationSelectors';
import ButtonMove from '../../../../components/ButtonMove/ButtonMove';

interface ITutorialListWidgetProps {
    className?: string,
    showHeader?: boolean,
    showTutorialInfo?: boolean,
    size?: 'sm' | 'lg',
    type?: TTutorialType,
}

function TutorialListWidget(props: ITutorialListWidgetProps) {
    const {
        className,
        showHeader,
        showTutorialInfo,
        size = 'sm',
        type = TUTORIAL_TYPES.LESSON,
    } = props;
    const { getLangPath } = useLangContext();
    const dispatch = useDispatch();
    const tutorialsList = useSelector(getTutorialsListSelector);
    const {
        scrollRef,
        onScrollLeft,
        onScrollRight,
    } = useHorizontalScroll<HTMLDivElement>({ withGrabCursor: false });
    const isPlatformMobile = useSelector(getIsLocationPlatformMobileSelector);

    useEffect(() => {
        dispatch(initTutorialsAction());
    }, [dispatch]);

    const tutorialsListToRender = useMemo(() => _.chain(tutorialsList)
            .filter(tutorial => !type || tutorial.type === type)
            .map((tutorial) => ({
                ...tutorial,
                isSeen: !!tutorial.seen_at,
                previewImgSrc: getAcademyLessonVideoPreviewImgSrcByUrl(tutorial.url),
            }))
            .orderBy(['isSeen', 'position'], ['asc', 'asc'])
            .value()
        , [tutorialsList, type]);

    const handleSeeAllClick = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({
            name: LOCATION_NAMES.TUTORIALS,
        }));
    }, [dispatch]);

    const handleCardClick = useCallback((e) => {
        const tutorial_name: string = e.currentTarget.dataset.name;
        dispatch(locationSlice.actions.openLocationAction({
            name: LOCATION_NAMES.TUTORIAL_VIDEO,
            searchParams: {
                tutorial_name,
            },
        }));
    }, [dispatch]);

    return tutorialsListToRender.length > 0 && <div className={classNames(className, 'position-relative')}>
        {
            !!showHeader && <TutorialListWidgetHeaderStyled>
                <h6 className={'mb-0'}>{getLangPath('tutorials.academy')}</h6>
                <SeeAllButton onClick={handleSeeAllClick}/>
            </TutorialListWidgetHeaderStyled>
        }
        <TutorialListWidgetCardsStyled ref={scrollRef}
                                       className={classNames('tutorials-list-widget__cards', {
                                           lg: size === 'lg',
                                           'hidden-scrollbar': isPlatformMobile,
                                           'custom-scrollbar': !isPlatformMobile,
                                           mobile: isPlatformMobile,
                                       })}>
            {
                tutorialsListToRender.map(tutorial => <div key={tutorial.name}>
                    <TutorialListWidgetCardStyled
                        className={classNames({
                            'is-seen': tutorial.isSeen,
                            lg: size === 'lg',
                        })}
                        data-name={tutorial.name}
                        imgUrl={tutorial.previewImgSrc}
                        onClick={handleCardClick}
                    >
                        <TutorialPlayIcon
                            isSeen={tutorial.isSeen}
                            size={size}
                        />
                    </TutorialListWidgetCardStyled>
                    {
                        !!showTutorialInfo && <TutorialListWidgetItemInfoStyled>
                            <TutorialListWidgetItemTitleStyled>
                                {getLangPath(`tutorials.video.${tutorial.name}.title`)}
                            </TutorialListWidgetItemTitleStyled>
                        </TutorialListWidgetItemInfoStyled>
                    }
                </div>)
            }
        </TutorialListWidgetCardsStyled>

        {
            !isPlatformMobile &&
            <TutorialListWidgetCardsScrollerStyled>
                <ButtonMove className={'backdrop-filter-blur100'} direction={'left'} size={'xs'} onClick={onScrollLeft}/>
                <ButtonMove className={'backdrop-filter-blur100'} direction={'right'} size={'xs'} onClick={onScrollRight}/>
            </TutorialListWidgetCardsScrollerStyled>
        }
    </div>;
}

export default TutorialListWidget;
